import React, { useState, useEffect, useCallback, useRef } from 'react';
import { eventsApi, unitsApi, callsApi, assignmentsApi, employeesApi } from '../services/api';
import { useWebSocket } from '../hooks/useWebSocket';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuSeparator,
} from '../components/ui/context-menu';
import { 
  Phone, MapPin, FileText, Send, User, AlertTriangle, 
  CheckCircle, Clock, ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';

const CALL_TYPES = [
  'Intoxication',
  'Drug Usage',
  'Unconscious',
  'Fall',
  'Trauma',
  'Allergic Reaction',
  'Sick',
  'Chest Pains',
  'Headache',
  'Minor Injury'
];

const STATUS_CONFIG = {
  enroute: { label: 'ENROUTE', color: 'text-white' },
  on_scene: { label: 'ON SCENE', color: 'text-red-500' },
  transport: { label: 'TRANSPORTING', color: 'text-yellow-400' },
  at_destination: { label: 'AT DESTINATION', color: 'text-orange-400' },
  available: { label: 'AVAILABLE', color: 'text-green-500' },
  at_post: { label: 'AT POST', color: 'text-gray-400' },
};

const STATUS_ORDER = ['enroute', 'on_scene', 'transport', 'at_destination', 'available', 'at_post'];

const MAP_PLACEHOLDER = "https://images.unsplash.com/photo-1587400563356-2f6cf901cfbb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHxkYXJrJTIwdGFjdGljYWwlMjBtYXAlMjBkYXNoYm9hcmR8ZW58MHx8fHwxNzc1NTI1MTk2fDA&ixlib=rb-4.1.0&q=85";

export default function DispatchPage() {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [availableUnits, setAvailableUnits] = useState([]);
  const [assignedUnits, setAssignedUnits] = useState([]);
  const [activeCalls, setActiveCalls] = useState([]);
  const [employees, setEmployees] = useState([]);
  
  // Call form state
  const [callType, setCallType] = useState('');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [eventsRes, unitsRes, callsRes, employeesRes] = await Promise.all([
        eventsApi.getActive(),
        unitsApi.getAll(selectedEvent?.id),
        callsApi.getActive(selectedEvent?.id),
        employeesApi.getAll()
      ]);
      
      setEvents(eventsRes.data);
      const units = unitsRes.data;
      setAvailableUnits(units.filter(u => ['available', 'at_post'].includes(u.status)));
      setAssignedUnits(units.filter(u => ['enroute', 'on_scene', 'transport', 'at_destination'].includes(u.status)));
      setActiveCalls(callsRes.data);
      setEmployees(employeesRes.data);
      
      if (!selectedEvent && eventsRes.data.length > 0) {
        setSelectedEvent(eventsRes.data[0]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [selectedEvent?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Track if context menu is open to prevent re-renders that close it
  const pendingRefresh = useRef(false);
  const contextMenuOpen = useRef(false);

  useWebSocket(useCallback((msg) => {
    if (['unit_status_change', 'call_created', 'call_updated', 'assignment_created'].includes(msg.type)) {
      if (contextMenuOpen.current) {
        pendingRefresh.current = true;
      } else {
        fetchData();
      }
    }
  }, [fetchData]));

  const handleContextMenuChange = useCallback((open) => {
    contextMenuOpen.current = open;
    if (!open && pendingRefresh.current) {
      pendingRefresh.current = false;
      fetchData();
    }
  }, [fetchData]);

  const handleCreateCall = async (e) => {
    e.preventDefault();
    if (!selectedEvent) {
      toast.error('Please select an event first');
      return;
    }
    if (!callType) {
      toast.error('Please select a call type');
      return;
    }
    if (!location.trim()) {
      toast.error('Please enter a location');
      return;
    }

    setSubmitting(true);
    try {
      await callsApi.create({
        event_id: selectedEvent.id,
        call_type: callType,
        location: location.trim(),
        notes: notes.trim()
      });
      toast.success('Call created successfully');
      setCallType('');
      setLocation('');
      setNotes('');
      fetchData();
    } catch (error) {
      toast.error('Failed to create call');
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignUnit = async (unitId, callId) => {
    try {
      await assignmentsApi.create(callId, unitId);
      toast.success('Unit assigned');
      fetchData();
    } catch (error) {
      toast.error('Failed to assign unit');
      console.error(error);
    }
  };

  const handleStatusChange = async (unitId, newStatus, transportDestination = null) => {
    try {
      await unitsApi.updateStatus(unitId, newStatus, transportDestination);
      const label = STATUS_CONFIG[newStatus]?.label || newStatus;
      const message = transportDestination ? `${label} / ${transportDestination}` : label;
      toast.success(`Status changed to ${message}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to change status');
      console.error(error);
    }
  };

  const handleCompleteCall = async (callId) => {
    try {
      await callsApi.update(callId, { status: 'completed' });
      toast.success('Call completed');
      fetchData();
    } catch (error) {
      toast.error('Failed to complete call');
      console.error(error);
    }
  };

  const handleDisposition = async (unitId, callId, disposition) => {
    try {
      await callsApi.setDisposition(callId, disposition, unitId);
      toast.success(`${disposition} - Unit moved to Available`);
      fetchData();
    } catch (error) {
      toast.error('Failed to set disposition');
      console.error(error);
    }
  };

  const handleCancelCall = async (callId) => {
    try {
      await callsApi.cancel(callId);
      toast.success('Call cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel call');
      console.error(error);
    }
  };

  const getPersonnelNames = (unit) => {
    if (!unit?.personnel || unit.personnel.length === 0) return '';
    return unit.personnel.map(empId => {
      const emp = employees.find(e => e.id === empId);
      return emp ? emp.name : 'Unknown';
    }).join(', ');
  };

  // Get medical areas for a unit's event
  const getMedicalAreas = (unit) => {
    const event = events.find(e => e.id === unit?.event_id) || selectedEvent;
    return (event?.medical_areas || []).filter(a => a && a.trim());
  };

  // Unit context menu for right-click on assigned units / jobs
  const UnitContextMenu = ({ unit, children }) => {
    const medicalAreas = getMedicalAreas(unit);
    const call = activeCalls.find(c => c.assigned_units?.includes(unit.id));
    const isOnCall = !!call;
    
    // If unit is NOT on a call, only allow Available <-> At Post
    const allowedStatuses = isOnCall ? STATUS_ORDER : ['available', 'at_post'];
    
    return (
      <ContextMenu onOpenChange={handleContextMenuChange}>
        <ContextMenuTrigger>{children}</ContextMenuTrigger>
        <ContextMenuContent className="bg-zinc-900 border-zinc-700 min-w-[200px]">
          <ContextMenuSub>
            <ContextMenuSubTrigger className="text-white">Change Status</ContextMenuSubTrigger>
            <ContextMenuSubContent className="bg-zinc-900 border-zinc-700">
              {allowedStatuses.map(status => {
                if (status === 'transport') {
                  if (medicalAreas.length === 0) {
                    return (
                      <ContextMenuItem
                        key={status}
                        onClick={() => handleStatusChange(unit.id, status)}
                        className={`${STATUS_CONFIG[status].color} hover:bg-zinc-800`}
                      >
                        {STATUS_CONFIG[status].label}
                      </ContextMenuItem>
                    );
                  }
                  return (
                    <ContextMenuSub key={status}>
                      <ContextMenuSubTrigger className={`${STATUS_CONFIG[status].color} hover:bg-zinc-800`}>
                        {STATUS_CONFIG[status].label}
                      </ContextMenuSubTrigger>
                      <ContextMenuSubContent className="bg-zinc-900 border-zinc-700">
                        {medicalAreas.map(area => (
                          <ContextMenuItem
                            key={area}
                            onClick={() => handleStatusChange(unit.id, status, area)}
                            className="text-yellow-400 hover:bg-zinc-800"
                          >
                            {area}
                          </ContextMenuItem>
                        ))}
                      </ContextMenuSubContent>
                    </ContextMenuSub>
                  );
                }
                return (
                  <ContextMenuItem
                    key={status}
                    onClick={() => handleStatusChange(unit.id, status)}
                    className={`${STATUS_CONFIG[status].color} hover:bg-zinc-800`}
                  >
                    {STATUS_CONFIG[status].label}
                  </ContextMenuItem>
                );
              })}
            </ContextMenuSubContent>
          </ContextMenuSub>
          {/* Disposition options */}
          {call && !['available', 'at_post'].includes(unit.status) && (
            <>
              <ContextMenuSeparator className="bg-zinc-700" />
              <ContextMenuItem onClick={() => handleDisposition(unit.id, call.id, 'RMA/AMA')} className="text-cyan-400 hover:bg-zinc-800">RMA/AMA</ContextMenuItem>
              <ContextMenuItem onClick={() => handleDisposition(unit.id, call.id, 'UNFOUNDED')} className="text-cyan-400 hover:bg-zinc-800">UNFOUNDED</ContextMenuItem>
              <ContextMenuItem onClick={() => handleDisposition(unit.id, call.id, 'LEFT PRIOR')} className="text-cyan-400 hover:bg-zinc-800">LEFT PRIOR</ContextMenuItem>
              <ContextMenuSeparator className="bg-zinc-700" />
              <ContextMenuItem onClick={() => handleCancelCall(call.id)} className="text-red-500 hover:bg-red-500/10">Cancel Call</ContextMenuItem>
            </>
          )}
        </ContextMenuContent>
      </ContextMenu>
    );
  };

  // Call/Job context menu
  const CallContextMenu = ({ call, children }) => (
    <ContextMenu onOpenChange={handleContextMenuChange}>
      <ContextMenuTrigger>{children}</ContextMenuTrigger>
      <ContextMenuContent className="bg-zinc-900 border-zinc-700 min-w-[200px]">
        <ContextMenuItem
          onClick={() => handleCompleteCall(call.id)}
          className="text-green-500 hover:bg-zinc-800"
        >
          Complete Call
        </ContextMenuItem>
        {availableUnits.length > 0 && (
          <>
            <ContextMenuSeparator className="bg-zinc-700" />
            <ContextMenuSub>
              <ContextMenuSubTrigger className="text-blue-400">Assign Unit</ContextMenuSubTrigger>
              <ContextMenuSubContent className="bg-zinc-900 border-zinc-700">
                {availableUnits.map(u => (
                  <ContextMenuItem
                    key={u.id}
                    onClick={() => handleAssignUnit(u.id, call.id)}
                    className="text-white hover:bg-zinc-800"
                  >
                    {u.unit_id}
                  </ContextMenuItem>
                ))}
              </ContextMenuSubContent>
            </ContextMenuSub>
          </>
        )}
        <ContextMenuSeparator className="bg-zinc-700" />
        <ContextMenuItem
          onClick={() => handleCancelCall(call.id)}
          className="text-red-500 hover:bg-red-500/10"
        >
          Cancel Call
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );

  return (
    <div className="min-h-screen bg-zinc-950 p-4" data-testid="dispatch-page">
      {/* Event Selector */}
      <div className="mb-4 flex items-center gap-4">
        <Label className="text-zinc-300">Active Event:</Label>
        <Select 
          value={selectedEvent?.id || ''} 
          onValueChange={(val) => setSelectedEvent(events.find(e => e.id === val))}
        >
          <SelectTrigger className="w-64 bg-zinc-800 border-zinc-600 text-white" data-testid="event-selector">
            <SelectValue placeholder="Select Event" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-zinc-600">
            {events.map(event => (
              <SelectItem key={event.id} value={event.id} className="text-white hover:bg-zinc-700">
                {event.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {events.length === 0 && (
          <span className="text-zinc-500 text-sm">No active events. Create one in Admin panel.</span>
        )}
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Side - Call Input + Units (stacked) */}
        <div className="flex flex-col gap-4">
          {/* Call Input Form */}
          <Card className="bg-zinc-900 border-zinc-700">
            <CardHeader className="border-b border-zinc-700 pb-4">
              <CardTitle className="font-heading text-lg md:text-xl text-white flex items-center gap-2">
                <Phone className="w-5 h-5 text-blue-500" />
                New Emergency Call
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 md:p-6">
              <form onSubmit={handleCreateCall} className="space-y-4">
                {/* Call Type */}
                <div className="space-y-2">
                  <Label className="text-zinc-300 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    Call Type
                  </Label>
                  <Select value={callType} onValueChange={setCallType}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-600 text-white" data-testid="call-type-select">
                      <SelectValue placeholder="Select call type" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-600">
                      {CALL_TYPES.map(type => (
                        <SelectItem key={type} value={type} className="text-white hover:bg-zinc-700">
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Location */}
                <div className="space-y-2">
                  <Label className="text-zinc-300 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-emerald-500" />
                    Location
                  </Label>
                  <Input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="Enter location within facility..."
                    className="bg-zinc-800 border-zinc-600 text-white placeholder:text-zinc-500"
                    data-testid="location-input"
                  />
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label className="text-zinc-300 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-500" />
                    Notes
                  </Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Additional details about the emergency..."
                    className="bg-zinc-800 border-zinc-600 text-white placeholder:text-zinc-500 min-h-[80px]"
                    data-testid="notes-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting || !selectedEvent}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3"
                  data-testid="create-call-button"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {submitting ? 'Creating Call...' : 'Dispatch Call'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Available Units */}
          <Card className="bg-zinc-900 border-zinc-700 flex-1">
            <CardHeader className="border-b border-zinc-700 pb-3 py-3">
              <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                Available Units
                <Badge className="ml-auto bg-emerald-500/20 text-emerald-500">
                  {availableUnits.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <ScrollArea className="h-[120px]">
                {availableUnits.length === 0 ? (
                  <p className="text-zinc-500 text-sm">No available units</p>
                ) : (
                  <div className="space-y-2">
                    {availableUnits.map(unit => (
                      <div 
                        key={unit.id}
                        className="bg-zinc-800 border border-zinc-700 p-2 rounded-sm flex items-center justify-between"
                        data-testid={`available-unit-${unit.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-zinc-400" />
                          <span className="text-zinc-300 text-sm font-medium">{unit.unit_id}</span>
                          <span className="text-zinc-500 text-xs">{getPersonnelNames(unit)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Active Calls / Jobs */}
          <Card className="bg-zinc-900 border-zinc-700 flex-1">
            <CardHeader className="border-b border-zinc-700 pb-3 py-3">
              <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
                <Phone className="w-4 h-4 text-red-500" />
                Active Jobs
                <Badge className="ml-auto bg-red-500/20 text-red-500">
                  {activeCalls.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <ScrollArea className="h-[120px]">
                {activeCalls.length === 0 ? (
                  <p className="text-zinc-500 text-sm">No active jobs</p>
                ) : (
                  <div className="space-y-2">
                    {activeCalls.map(call => {
                      const callUnits = assignedUnits.filter(u => call.assigned_units?.includes(u.id));
                      const isPending = !call.assigned_units || call.assigned_units.length === 0;
                      const isOverdue = isPending && call.created_at && (new Date() - new Date(call.created_at)) > 120000;
                      return (
                        <CallContextMenu key={call.id} call={call}>
                          <div
                            className={`bg-zinc-800 border p-2 rounded-sm cursor-pointer hover:bg-zinc-700/50 ${isPending ? (isOverdue ? 'border-yellow-400 pending-flash' : 'border-yellow-500/50') : 'border-red-500/30'}`}
                            data-testid={`active-call-${call.id}`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-amber-400 text-sm font-medium">{call.call_type}</span>
                              {isPending ? (
                                <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">PENDING</Badge>
                              ) : (
                                <Badge className="bg-zinc-700 text-zinc-300 text-xs">
                                  {callUnits.length} unit{callUnits.length !== 1 ? 's' : ''}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-1 text-xs text-zinc-400">
                              <MapPin className="w-3 h-3" />
                              {call.location}
                            </div>
                            {callUnits.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {callUnits.map(u => (
                                  <Badge key={u.id} className="bg-blue-500/15 text-blue-400 text-xs">
                                    {u.unit_id}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </CallContextMenu>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* On Assignment */}
          <Card className="bg-zinc-900 border-zinc-700 flex-1">
            <CardHeader className="border-b border-zinc-700 pb-3 py-3">
              <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-500" />
                On Assignment
                <Badge className="ml-auto bg-amber-500/20 text-amber-500">
                  {assignedUnits.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <ScrollArea className="h-[120px]">
                {assignedUnits.length === 0 ? (
                  <p className="text-zinc-500 text-sm">No units on assignment</p>
                ) : (
                  <div className="space-y-2">
                    {assignedUnits.map(unit => {
                      const call = activeCalls.find(c => c.assigned_units?.includes(unit.id));
                      return (
                        <UnitContextMenu key={unit.id} unit={unit}>
                          <div 
                            className="bg-zinc-800 border border-amber-500/30 p-2 rounded-sm flex items-center gap-2 cursor-pointer hover:bg-zinc-700/50"
                            data-testid={`assigned-unit-${unit.id}`}
                          >
                            <User className="w-4 h-4 text-amber-500" />
                            <span className="text-zinc-300 text-sm font-medium">{unit.unit_id}</span>
                            <span className={`text-xs ${STATUS_CONFIG[unit.status]?.color || 'text-zinc-400'}`}>
                              [{STATUS_CONFIG[unit.status]?.label || unit.status}]
                            </span>
                            {call && (
                              <span className="text-zinc-500 text-xs ml-auto">{call.call_type}</span>
                            )}
                          </div>
                        </UnitContextMenu>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Right Side - Map Area */}
        <Card className="bg-zinc-900 border-zinc-700 h-full">
          <CardHeader className="border-b border-zinc-700 pb-4">
            <CardTitle className="font-heading text-xl text-white flex items-center gap-2">
              <MapPin className="w-5 h-5 text-emerald-500" />
              Event Map
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 h-[calc(100%-80px)]">
            {selectedEvent?.map_data ? (
              selectedEvent.map_type === 'image' ? (
                <img 
                  src={selectedEvent.map_data} 
                  alt="Event Map" 
                  className="w-full h-full object-contain rounded-sm"
                  data-testid="event-map-image"
                />
              ) : (
                <div className="w-full h-full bg-zinc-800 rounded-sm flex items-center justify-center">
                  <p className="text-zinc-400">KML Map Viewer</p>
                </div>
              )
            ) : (
              <div className="w-full h-full bg-zinc-800 rounded-sm flex flex-col items-center justify-center relative overflow-hidden">
                <img 
                  src={MAP_PLACEHOLDER} 
                  alt="Map Placeholder" 
                  className="absolute inset-0 w-full h-full object-cover opacity-30"
                />
                <div className="relative z-10 text-center">
                  <ImageIcon className="w-12 h-12 text-zinc-500 mx-auto mb-2" />
                  <p className="text-zinc-400">No map uploaded for this event</p>
                  <p className="text-zinc-500 text-sm">Upload via Admin panel</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
